/**
 * probeMcpOAuthConnected — the FE's `is this MCP server connected?` check for
 * the `oauth-connect` custom interaction (mirrors connections-probe.ts's
 * probeIsToolkitConnected: raw fetch + a Bearer token, no cache — the
 * settle loop it feeds already dedupes calls via its grace schedule).
 *
 * Matches useAuthConnectState's `(token, key) => Promise<boolean>` probe
 * contract so OAuthConnectInput can pass this in directly as
 * `useAuthConnectState(slug, probeMcpOAuthConnected)` alongside the
 * default `probeIsToolkitConnected(token, toolkit)` used by Composio
 * callers.
 *
 * GET /api/mcps/oauth/status?slug — authenticated; returns
 * `{ connected: boolean }` from whether a token row exists server-side,
 * never the token itself (mcps-oauth-routes.handler.ts).
 *
 * Errors swallowed — returns `false` on any failure (the caller treats this
 * as "not yet confirmed connected", the safe default).
 */

import { apiUrl } from "@/lib/api/base";
import { authedFetch } from "@/lib/api/client";

export async function probeMcpOAuthConnected(
  token: string,
  slug: string
): Promise<boolean> {
  try {
    const r = await fetch(
      apiUrl(`/api/mcps/oauth/status?slug=${encodeURIComponent(slug)}`),
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!r.ok) return false;
    const json = (await r.json()) as { connected?: boolean };
    return json.connected === true;
  } catch {
    return false;
  }
}

/**
 * mintMcpOAuthUrl — mint a FRESH authorize URL for an MCP server at click time.
 *
 * POST /api/mcps/connect { slug } is an idempotent converge for an already-
 * installed catalog OAuth server (mcps-connect-routes.handler.ts): it re-uses
 * the existing row and mints fresh PKCE state per call, replying
 * `{ needs_auth: true, authUrl }`. Structured read only — an authUrl is
 * returned iff the reply says `needs_auth === true` with a real string; any
 * other successful reply (already connected, manual setup required) carries
 * no fresh URL and resolves `{}` so the caller can settle without opening a
 * tab. Throws on transport/HTTP failure (authedFetch) — the caller's cue to
 * fall back to its stored URL.
 */
export async function mintMcpOAuthUrl(
  slug: string
): Promise<{ authUrl?: string }> {
  const res = await authedFetch("/api/mcps/connect", {
    method: "POST",
    body: JSON.stringify({ slug }),
  });
  const json = (await res.json()) as {
    needs_auth?: unknown;
    authUrl?: unknown;
  };
  return json.needs_auth === true &&
    typeof json.authUrl === "string" &&
    json.authUrl !== ""
    ? { authUrl: json.authUrl }
    : {};
}

/**
 * What severing an MCP connection actually achieved.
 *  - `revoked`       the provider confirmed the grant is dead (RFC 7009 2xx).
 *  - `not_supported` the provider offers no revocation endpoint, or there was
 *                    no stored grant. Vestra's copy is gone either way.
 *  - `failed`        we could not reach the provider. Vestra's copy is gone,
 *                    the provider may still hold one.
 *  - `unreachable`   the disconnect call itself did not complete, so we know
 *                    nothing. Never report this as a disconnect.
 */
export type McpSeverOutcome =
  | "revoked"
  | "not_supported"
  | "failed"
  | "unreachable";

/**
 * severMcpConnection — DELETE /api/mcps/:slug/oauth.
 *
 * The counterpart to mintMcpOAuthUrl: it revokes at the provider where that is
 * possible, drops Vestra's stored grant unconditionally, and moves the
 * credentials epoch so a warm runner stops using the bearer it cached.
 *
 * Structured read only, and deliberately no throw: removing the connection row
 * must still happen when this leg fails. The caller renders the outcome, so an
 * unreachable sever can never be shown to the user as a clean one.
 */
export async function severMcpConnection(
  slug: string
): Promise<McpSeverOutcome> {
  try {
    const res = await authedFetch(
      `/api/mcps/${encodeURIComponent(slug)}/oauth`,
      {
        method: "DELETE",
      }
    );
    if (!res.ok) return "unreachable";
    const json = (await res.json()) as { provider_revocation?: unknown };
    return json.provider_revocation === "revoked"
      ? "revoked"
      : json.provider_revocation === "failed"
      ? "failed"
      : "not_supported";
  } catch {
    return "unreachable";
  }
}
